// Fields.js
import React, { useEffect, useCallback } from "react";
import { Row, Col, Form, Button } from "react-bootstrap";
import {
  FaPlus,
  FaTrash,
  FaCompressAlt,
  FaExpandAlt,
} from "react-icons/fa";
import { v4 as uuidv4 } from "uuid";

// —————————————————————————
// ۱) DefaultField: یک فیلد متنی ساده
// —————————————————————————
const DefaultField = React.memo(({ title, name, data, set_data }) => (
  <Row className="mb-3">
    <Col>
      <Form.Group>
        <Form.Label>{title.fa}</Form.Label>
        <Form.Control
          type="text"
          value={data[name] || ""}
          onChange={e => {
            const val = e.target.value;
            set_data(prev => ({ ...prev, [name]: val }));
          }}
        />
      </Form.Group>
    </Col>
  </Row>
));

// —————————————————————————
// ۲) DateField: برای issued و accessed
// —————————————————————————
const DateField = React.memo(({ title, name, data, set_data }) => {
  // استخراج سه بخش سال-ماه-روز
  const raw = data[name]?.["date-parts"]?.[0]?.[0] || "";
  const parts = raw.split("-");
  const year = parts[0] || "";
  const month = parts[1] || "";
  const day = parts[2] || "";

  const onChange = (part, value) => {
    // فقط همون بخش را تعویض کن، بقیه را نگه دار
    const newYear = part === "year" ? value : year;
    const newMonth = part === "month" ? value : month;
    const newDay = part === "day" ? value : day;
    // همیشه سه‌تایی بسازیم تا اندیس‌ها ثابت باشند
    const dateString = `${newYear}-${newMonth}-${newDay}`;
    set_data(prev => ({
      ...prev,
      [name]: { "date-parts": [[dateString]] },
    }));
  };

  return (
    <>
      <Row>
        <Col className="text-right mb-1">{title.fa}</Col>
      </Row>
      <Row className="mb-3">
        <Col>
          <Form.Control
            type="text"
            placeholder="روز"
            value={day}
            onChange={e => onChange("day", e.target.value)}
          />
        </Col>
        <Col>
          <Form.Control
            type="text"
            placeholder="ماه"
            value={month}
            onChange={e => onChange("month", e.target.value)}
          />
        </Col>
        <Col>
          <Form.Control
            type="text"
            placeholder="سال"
            value={year}
            onChange={e => onChange("year", e.target.value)}
          />
        </Col>
      </Row>
    </>
  );
});

// —————————————————————————
// ۳) CreatorsField: فیلد پدیدآوردگان
// —————————————————————————
const CreatorsField = React.memo(({ title, positions, data, set_data }) => {
  const authors = data.author || [];
  const defaultPos = positions[0]?.name || "";

  const addAuthor = useCallback(() => {
    set_data(prev => ({
      ...prev,
      author: [
        ...prev.author,
        {
          id: uuidv4(),
          type: "fullname",
          given: "",
          family: "",
          position: defaultPos,
        },
      ],
    }));
  }, [set_data, defaultPos]);

  const updateAuthor = useCallback((id, key, value) => {
    set_data(prev => ({
      ...prev,
      author: prev.author.map(a =>
        a.id === id ? { ...a, [key]: value } : a
      ),
    }));
  }, [set_data]);

  const changeType = useCallback(id => {
    set_data(prev => ({
      ...prev,
      author: prev.author.map(a =>
        a.id !== id
          ? a
          : a.type === "fullname"
          ? { id, type: "literal", literal: "" }
          : { id, type: "fullname", given: "", family: "", position: defaultPos }
      ),
    }));
  }, [set_data, defaultPos]);

  const removeAuthor = useCallback(id => {
    set_data(prev => ({
      ...prev,
      author: prev.author.filter(a => a.id !== id),
    }));
  }, [set_data]);

  return (
    <>
      <Row>
        <Col className="text-right mb-2">{title.fa}</Col>
      </Row>
      {authors.map((a, idx) => {
        const isLast = idx === authors.length - 1;
        return (
          <Row key={a.id} className="align-items-center mb-2">
            <Col md={4}>
              <Form.Control
                as="select"
                value={a.position}
                onChange={e =>
                  updateAuthor(a.id, "position", e.target.value)
                }
              >
                {positions.map(p => (
                  <option key={p.name} value={p.name}>
                    {p.title.fa}
                  </option>
                ))}
              </Form.Control>
            </Col>
            {a.type === "fullname" ? (
              <>
                <Col md={3}>
                  <Form.Control
                    placeholder="نام"
                    value={a.given}
                    onChange={e =>
                      updateAuthor(a.id, "given", e.target.value)
                    }
                  />
                </Col>
                <Col md={3}>
                  <Form.Control
                    placeholder="نام خانوادگی"
                    value={a.family}
                    onChange={e =>
                      updateAuthor(a.id, "family", e.target.value)
                    }
                  />
                </Col>
              </>
            ) : (
              <Col md={6}>
                <Form.Control
                  placeholder="نام کامل"
                  value={a.literal}
                  onChange={e =>
                    updateAuthor(a.id, "literal", e.target.value)
                  }
                />
              </Col>
            )}
            <Col md={1}>
              <Button
                block
                variant="outline-info"
                onClick={() => changeType(a.id)}
              >
                {a.type === "fullname" ? <FaCompressAlt /> : <FaExpandAlt />}
              </Button>
            </Col>
            <Col md={1}>
              <Button
                block
                variant={isLast ? "outline-success" : "outline-danger"}
                onClick={() =>
                  isLast ? addAuthor() : removeAuthor(a.id)
                }
              >
                {isLast ? <FaPlus /> : <FaTrash />}
              </Button>
            </Col>
          </Row>
        );
      })}
    </>
  );
});

// —————————————————————————
// ۴) نگاشت انواع فیلدها به کامپوننت
// —————————————————————————
const inputsMap = {
  creators: CreatorsField,
  issued: props => <DateField name="issued" {...props} />,
  accessed: props => <DateField name="accessed" {...props} />,
  // بقیهٔ فیلدهای خاص را اینجا اضافه کنید
};

const Fields = ({ schema, data, set_data }) => {
  // مقداردهی اولیه فقط یک‌بار
  useEffect(() => {
    if (!data.language) {
      set_data(prev => ({ ...prev, language: "fa" }));
    }
    if (
      schema.some(f => f.name === "creators") &&
      (!data.author || !data.author.length)
    ) {
      const pos = schema.find(f => f.name === "creators").positions[0].name;
      set_data(prev => ({
        ...prev,
        author: [
          {
            id: uuidv4(),
            type: "fullname",
            given: "",
            family: "",
            position: pos,
          },
        ],
      }));
    }
    // eslint-disable-next-line
  }, [schema, data.author, set_data]);

  return (
    <>
      {/* ثابت: زبان و عنوان */}
      <Row className="text-right mb-3">
        <Col md={12}>
          <Form.Group>
            <Form.Label>زبان</Form.Label>
            <Form.Control
              as="select"
              value={data.language}
              onChange={e => {
                const val = e.target.value;
                set_data(prev => ({ ...prev, language: val }));
              }}
            >
              <option value="fa">فارسی</option>
              <option value="en">انگلیسی</option>
              <option value="ar">عربی</option>
            </Form.Control>
          </Form.Group>
        </Col>
        <Col md={12}>
          <Form.Group>
            <Form.Label>عنوان</Form.Label>
            <Form.Control
              type="text"
              value={data.title || ""}
              onChange={e => {
                const val = e.target.value;
                set_data(prev => ({ ...prev, title: val }));
              }}
            />
          </Form.Group>
        </Col>
      </Row>

      {/* رندر داینامیک بر اساس schema */}
      {schema.map(field => {
        const Comp = inputsMap[field.name] || DefaultField;
        return (
          <Comp
            key={field.name}
            {...field}
            name={field.name}
            data={data}
            set_data={set_data}
          />
        );
      })}
    </>
  );
};

export default Fields;
